import { useEffect } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  Text,
  useToastMessage,
} from '@equip.health/ui';

import { useLazyGetBulkProviderTeamAssignmentsLatestJobQuery } from '~/store/services/techOps';

import { TechOpsToasts } from './toastMessages';

const { success } = EquipUIFireflyV2Theme.colors;

type RecentlySubmittedJobPanelProps = {
  submittedJob: OpsBulkProviderJobResponse | null;
};

const RecentlySubmittedJobPanel = ({
  submittedJob,
}: RecentlySubmittedJobPanelProps) => {
  const { createToast } = useToastMessage();
  const [fetchLatestJob, { data: latestJob, isLoading, isFetching }] =
    useLazyGetBulkProviderTeamAssignmentsLatestJobQuery();

  useEffect(() => {
    fetchLatestJob()
      .unwrap()
      .catch(() => {
        createToast(TechOpsToasts.refreshJob.error());
      });
  }, [fetchLatestJob]);

  const handleRefresh = () => {
    fetchLatestJob()
      .unwrap()
      .catch(() => {
        createToast(TechOpsToasts.refreshJob.error());
      });
  };

  const displayJob = latestJob ?? submittedJob;

  return (
    <ChakraBox
      background="neutral.background.secondary"
      borderRadius="8px"
      marginBottom="32px"
      padding="24px"
    >
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        marginBottom="16px"
      >
        <Text variant="h5">Latest Submitted Job</Text>
        <FireflyButton
          id="latest-submitted-job-refresh"
          isDisabled={isLoading || isFetching}
          isLoading={isLoading || isFetching}
          leftIcon={FAIconName.arrowsRotateRegular}
          onClick={handleRefresh}
          variant="tertiary"
        >
          Refresh
        </FireflyButton>
      </ChakraFlex>

      {(isLoading || isFetching) && (
        <Text color="neutral.secondary" variant="body">
          Loading...
        </Text>
      )}

      {!(isLoading || isFetching) && displayJob && (
        <ChakraBox background="success.10" borderRadius="8px" padding="16px">
          <ChakraFlex
            borderBottom="1px solid"
            borderColor="neutral.border.secondary"
            justifyContent="space-between"
            paddingBottom="12px"
            paddingTop="12px"
          >
            <Text color="neutral.secondary" variant="body">
              Job Key (save for reference):
            </Text>
            <Text variant="body">{displayJob.jobKey}</Text>
          </ChakraFlex>

          <ChakraFlex
            borderBottom="1px solid"
            borderColor="neutral.border.secondary"
            justifyContent="space-between"
            paddingBottom="12px"
            paddingTop="12px"
          >
            <Text color="neutral.secondary" variant="body">
              Status:
            </Text>
            <Text variant="body">{displayJob.status}</Text>
          </ChakraFlex>

          <ChakraFlex
            borderBottom="1px solid"
            borderColor="neutral.border.secondary"
            justifyContent="space-between"
            paddingBottom="12px"
            paddingTop="12px"
          >
            <Text color="neutral.secondary" variant="body">
              Total Patients:
            </Text>
            <Text variant="body">{displayJob.totalPatients}</Text>
          </ChakraFlex>

          <ChakraFlex
            justifyContent="space-between"
            paddingBottom="12px"
            paddingTop="12px"
          >
            <Text color="neutral.secondary" variant="body">
              Message:
            </Text>
            <Text color={success.base} variant="body">
              {displayJob.message}
            </Text>
          </ChakraFlex>
        </ChakraBox>
      )}

      {!(isLoading || isFetching) && !displayJob && (
        <Text color="neutral.secondary" variant="body">
          No latest job found
        </Text>
      )}
    </ChakraBox>
  );
};

export default RecentlySubmittedJobPanel;
